import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ShieldCheck, Shield, Smartphone, Mail as MailIcon, Key, RefreshCw,
  Copy, Check, Loader2, Download, AlertTriangle, Monitor, X
} from 'lucide-react';
import { formatApiError } from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecuritySettingsPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup flow state
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupMethod, setSetupMethod] = useState('totp');
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [setupBusy, setSetupBusy] = useState(false);

  // Recovery codes
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Disable / regenerate flows
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');

  const authHeader = () => {
    const token = localStorage.getItem('savenes_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/auth/mfa/status`, { headers: authHeader() });
      setStatus(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async (method) => {
    setSetupMethod(method);
    setSetupBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/mfa/setup`, { method }, { headers: authHeader() });
      setSetupData(r.data);
      setVerifyCode('');
      setSetupOpen(true);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSetupBusy(false); }
  };

  const verifySetup = async () => {
    setSetupBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/mfa/verify`, { code: verifyCode }, { headers: authHeader() });
      toast.success('MFA enabled');
      setRecoveryCodes(r.data.recovery_codes || []);
      setSetupOpen(false);
      setRecoveryOpen(true);
      loadStatus();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSetupBusy(false); }
  };

  const disableMfa = async () => {
    setSetupBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/mfa/disable`, { password: disablePassword }, { headers: authHeader() });
      toast.success('MFA disabled');
      setDisableOpen(false);
      setDisablePassword('');
      loadStatus();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSetupBusy(false); }
  };

  const regenerateCodes = async () => {
    setSetupBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/mfa/recovery-codes/regenerate`, { password: regeneratePassword }, { headers: authHeader() });
      setRecoveryCodes(r.data.recovery_codes || []);
      toast.success('New recovery codes generated');
      setRegenerateOpen(false);
      setRegeneratePassword('');
      setRecoveryOpen(true);
      loadStatus();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSetupBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const downloadCodes = () => {
    const blob = new Blob([`Savenes MFA Recovery Codes for ${user?.email}\nGenerated: ${new Date().toLocaleString()}\n\n${recoveryCodes.join('\n')}\n\nKeep these codes safe. Each code can be used only once.`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'savenes-recovery-codes.txt';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;

  const isEnabled = status?.enabled;
  const hasTotp = (status?.methods || []).includes('totp');
  const hasEmail = (status?.methods || []).includes('email');

  return (
    <div className="space-y-6" data-testid="security-settings-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Security</h1>
        <p className="text-[#64748B] mt-1">Manage multi-factor authentication and login security for your account.</p>
      </div>

      <Card className="careiq-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#166534]" />
            Multi-Factor Authentication (MFA)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${isEnabled ? 'bg-[#166534]/10 text-[#166534]' : 'bg-[#CA8A04]/10 text-[#CA8A04]'}`} data-testid="mfa-status-badge">
              {isEnabled ? <><Check className="w-3 h-3" />Enabled</> : <><AlertTriangle className="w-3 h-3" />Not enabled</>}
            </span>
            {isEnabled && status?.confirmed_at && (
              <span className="text-xs text-[#94A3B8]">since {new Date(status.confirmed_at).toLocaleDateString()}</span>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg border ${hasTotp ? 'border-[#166534]/30 bg-[#166534]/5' : 'border-[#E2E8F0]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-5 h-5 text-[#0E7490]" />
                <span className="font-semibold text-[#0A2342] text-sm">Authenticator App (TOTP)</span>
                {hasTotp && <Check className="w-4 h-4 text-[#166534] ml-auto" />}
              </div>
              <p className="text-xs text-[#64748B] mb-3">Google Authenticator, Authy, 1Password, etc. <span className="text-[#166534] font-medium">Recommended.</span></p>
              {!hasTotp && <Button size="sm" className="careiq-btn-primary" onClick={() => startSetup('totp')} disabled={setupBusy} data-testid="setup-totp-btn">Set up</Button>}
              {hasTotp && <span className="text-xs text-[#166534] font-medium">Active</span>}
            </div>

            <div className={`p-4 rounded-lg border ${hasEmail ? 'border-[#166534]/30 bg-[#166534]/5' : 'border-[#E2E8F0]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <MailIcon className="w-5 h-5 text-[#0E7490]" />
                <span className="font-semibold text-[#0A2342] text-sm">Email Code (OTP)</span>
                {hasEmail && <Check className="w-4 h-4 text-[#166534] ml-auto" />}
              </div>
              <p className="text-xs text-[#64748B] mb-3">6-digit code sent to {user?.email}. Good as a backup method.</p>
              {!hasEmail && <Button size="sm" className="careiq-btn-primary" onClick={() => startSetup('email')} disabled={setupBusy} data-testid="setup-email-btn">Set up</Button>}
              {hasEmail && <span className="text-xs text-[#166534] font-medium">Active</span>}
            </div>
          </div>

          {isEnabled && (
            <div className="pt-4 border-t border-[#E2E8F0] flex flex-wrap gap-2 items-center">
              <div className="flex items-center gap-2 text-xs text-[#64748B] mr-auto">
                <Key className="w-4 h-4" />
                Recovery codes remaining: <span className="font-semibold text-[#0A2342]">{status?.recovery_codes_remaining || 0}</span> / 10
              </div>
              <Button size="sm" variant="outline" onClick={() => setRegenerateOpen(true)} data-testid="regenerate-codes-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Regenerate codes</Button>
              <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={() => setDisableOpen(true)} data-testid="disable-mfa-btn">Disable MFA</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isEnabled && <TrustedDevicesSection />}

      {/* Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mfa-setup-dialog">
          <DialogHeader><DialogTitle>{setupMethod === 'totp' ? 'Set up Authenticator App' : 'Verify Email Code'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {setupMethod === 'totp' && setupData?.qr_image_b64 && (
              <>
                <p className="text-sm text-[#64748B]">1. Scan this QR with your authenticator app:</p>
                <div className="flex justify-center bg-white p-4 rounded-lg border"><img src={setupData.qr_image_b64} alt="QR" className="w-48 h-48" data-testid="mfa-qr-image" /></div>
                <p className="text-xs text-[#64748B]">Or enter this secret manually: <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded text-[10px] font-mono break-all">{setupData.secret}</code></p>
              </>
            )}
            {setupMethod === 'email' && (
              <p className="text-sm text-[#64748B]">We sent a 6-digit code to <span className="font-medium text-[#0A2342]">{setupData?.sent_to || user?.email}</span>. Enter it below.</p>
            )}
            <div>
              <Label>{setupMethod === 'totp' ? '2. Enter the 6-digit code from your app' : 'Verification code'}</Label>
              <Input type="text" value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="000000" className="text-center text-2xl tracking-widest font-mono" autoFocus data-testid="mfa-verify-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setSetupOpen(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={verifySetup} disabled={setupBusy || !verifyCode} data-testid="mfa-verify-submit">{setupBusy ? 'Verifying...' : 'Verify & enable'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recovery Codes Dialog */}
      <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
        <DialogContent className="sm:max-w-md" data-testid="recovery-codes-dialog">
          <DialogHeader><DialogTitle>Save your recovery codes</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#FEF3C7] border border-[#FCD34D] p-3 rounded-lg text-xs text-[#92400E] flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Save these codes somewhere safe. Each code can be used <strong>only once</strong> to access your account if you lose your device. Regenerating invalidates all previous codes.</span>
            </div>
            <div className="grid grid-cols-2 gap-2 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
              {recoveryCodes.map((c, i) => (
                <div key={i} className="font-mono text-sm text-[#0A2342] text-center py-1.5 bg-white border border-[#E2E8F0] rounded" data-testid={`recovery-code-${i}`}>{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={copyCodes} data-testid="copy-codes-btn">{copied ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}</Button>
              <Button variant="outline" className="flex-1" onClick={downloadCodes} data-testid="download-codes-btn"><Download className="w-4 h-4 mr-1" />Download</Button>
            </div>
            <Button className="w-full careiq-btn-primary" onClick={() => setRecoveryOpen(false)} data-testid="recovery-codes-done">I've saved my codes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="sm:max-w-md" data-testid="disable-mfa-dialog">
          <DialogHeader><DialogTitle>Disable MFA</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">Enter your password to disable MFA. You'll no longer need a code to log in.</p>
            <div><Label>Password</Label><Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} data-testid="disable-password-input" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDisableOpen(false)}>Cancel</Button><Button className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white" onClick={disableMfa} disabled={setupBusy || !disablePassword} data-testid="disable-confirm-btn">Disable</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate Codes Dialog */}
      <Dialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="regenerate-codes-dialog">
          <DialogHeader><DialogTitle>Regenerate Recovery Codes</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">Previously generated codes will be invalidated.</p>
            <div><Label>Password</Label><Input type="password" value={regeneratePassword} onChange={e => setRegeneratePassword(e.target.value)} data-testid="regenerate-password-input" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setRegenerateOpen(false)}>Cancel</Button><Button className="careiq-btn-primary" onClick={regenerateCodes} disabled={setupBusy || !regeneratePassword} data-testid="regenerate-confirm-btn">Generate new codes</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TrustedDevicesSection() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem('savenes_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/auth/trusted-devices`, { headers: authHeader() });
      setDevices(r.data.devices || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/auth/trusted-devices/${id}`, { headers: authHeader() });
      toast.success('Device revoked');
      load();
    } catch { toast.error('Failed to revoke'); }
    finally { setBusy(false); }
  };

  const revokeAll = async () => {
    if (!window.confirm('Revoke all trusted devices? They will need MFA on next login.')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/trusted-devices/revoke-all`, {}, { headers: authHeader() });
      toast.success(`Revoked ${r.data.revoked} device(s)`);
      load();
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="careiq-card" data-testid="trusted-devices-section">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
          <Monitor className="w-5 h-5 text-[#0E7490]" />
          Trusted Devices
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#64748B]">Devices where you chose "Remember this device" during MFA. They'll skip the MFA code for 30 days.</p>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#0E7490]" /> : devices.length === 0 ? (
          <p className="text-xs text-[#94A3B8] py-2">No trusted devices yet.</p>
        ) : (
          <div className="space-y-2">
            {devices.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`device-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0A2342]">{d.device_name}</div>
                  <div className="text-xs text-[#64748B] truncate">Last used: {d.last_used_at ? new Date(d.last_used_at).toLocaleString() : 'never'} • Expires: {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : ''}</div>
                </div>
                <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={() => revoke(d.id)} disabled={busy} data-testid={`revoke-device-${d.id}`}>
                  <X className="w-3.5 h-3.5 mr-1" />Revoke
                </Button>
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <Button variant="outline" size="sm" className="text-[#991B1B]" onClick={revokeAll} disabled={busy} data-testid="revoke-all-devices">Revoke all</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
